/**
 * Balanced ternary logic primitive.
 *
 * Values: T (+1), U (0), F (-1).
 * Port of ternary_logic.py's Ternary class.
 *
 * Uses flyweight singletons — there are exactly three instances.
 */

export type TernaryValue = 1 | 0 | -1;

export class Ternary {
  readonly value: TernaryValue;

  private static readonly _T = new Ternary(1);
  private static readonly _U = new Ternary(0);
  private static readonly _F = new Ternary(-1);

  private constructor(v: TernaryValue) {
    this.value = v;
  }

  /** Get the singleton for a given ternary value. */
  static of(v: TernaryValue): Ternary {
    if (v === 1) return Ternary._T;
    if (v === -1) return Ternary._F;
    return Ternary._U;
  }

  /** Normalize any JS value to a Ternary. */
  static from(v: unknown): Ternary {
    if (v instanceof Ternary) return v;
    if (typeof v === "boolean") return v ? Ternary._T : Ternary._F;
    if (typeof v === "number") {
      if (v > 0) return Ternary._T;
      if (v < 0) return Ternary._F;
      return Ternary._U;
    }
    if (typeof v === "string") {
      const clean = v.trim().toLowerCase();
      if (["true", "t", "1", "on", "yes"].includes(clean)) return Ternary._T;
      if (["false", "f", "-1", "off", "no"].includes(clean)) return Ternary._F;
      return Ternary._U;
    }
    if (v === null || v === undefined) return Ternary._U;
    return Ternary._U;
  }

  get isTrue(): boolean { return this.value === 1; }
  get isFalse(): boolean { return this.value === -1; }
  get isUnknown(): boolean { return this.value === 0; }

  /** Ternary NOT: invert sign (T↔F, U↔U). */
  not(): Ternary { return Ternary.of(-this.value as TernaryValue); }

  /** Ternary AND: min(a, b). */
  and(other: Ternary): Ternary {
    return Ternary.of(Math.min(this.value, other.value) as TernaryValue);
  }

  /** Ternary OR: max(a, b). */
  or(other: Ternary): Ternary {
    return Ternary.of(Math.max(this.value, other.value) as TernaryValue);
  }

  /** Ternary XOR: (a or b) and not (a and b). */
  xor(other: Ternary): Ternary {
    return this.or(other).and(this.and(other).not());
  }

  /** Numeric coercion for mixed arithmetic: T→+1, U→0, F→-1. */
  toNumber(): number { return this.value; }

  /** Display form. */
  toString(): string {
    if (this.value === 1) return "T";
    if (this.value === -1) return "F";
    return "U";
  }

  /** Equality check. */
  equals(other: Ternary): boolean { return this.value === other.value; }
}

/** Singleton constants. */
export const T = Ternary.of(1);
export const U = Ternary.of(0);
export const F = Ternary.of(-1);
